import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AccessBlocked, CtyButton, CtyHeader, ReleaseNotesFooterLink, Sidenav, useScreenWidth, useTheme } from "@circuitry-ai/doc-ui";
import { Image, useDisclosure } from '@nextui-org/react';
import SidebarItems from '../../assets/Sidebaritems';
import { PressEvent } from '@react-types/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@iconify/react';
import { BUILD_NUMBER } from '../../buildInfo';
import { useSelector } from 'react-redux';
import { clearTenantDetailsResponse, decodeJWT, RootState, useAppDispatch, useGetFgaUserResourcesQuery } from '@circuitry-ai/doc-data';
type Props = {
  children: React.ReactNode;
};
type CustomTitles = {
  [key: string]: string;
};
const customTitles: CustomTitles = {
  dashboard: "Dashboard",
  advisors: "Advisors",
  conversations: "Conversations",
  knowledge: "Knowledge",
  data: "Data",
  analytics: "AInalysts",
  pdfscanner: "Pdf Scanner",
  ocrscanner: "OCR Scanner",
  automation: "Automations",
  'schema-mapper': "Schema Mapper",
  settings: "Settings",
  analyst: "AInalysts",

};
// Mapping between pathname segments and API resource names
const PATHNAME_TO_RESOURCE: Record<string, string> = {
  '': 'dashboard', // root path '/'
  'dashboard': 'dashboard',
  'advisors': 'advisors',
  'analytics': 'analysts', // pathname is /analytics but resource is 'analysts'
  'agents': 'agents',
  'schema-mapper': 'agents',
  'aiworkers': 'journeys', // pathname contains 'aiworkers' but resource is 'specialists'
  'knowledge': 'knowledge',
  'actions': 'actions',
  'data': 'data',
  'conversations': 'conversations',
  'history': 'agent_history',
  'triage': 'triage_feedback',
  'uxgenie': 'uxgenie',
  'formgenie': 'uxgenie', // reuse uxgenie resource until formgenie has its own FGA entry
  'genie': 'uxgenie',
  'scheduler': 'scheduler',
  'settings': 'settings',
};

/** Application resource id required for FGA route gate (Layout), or undefined if no gate. */
function getRequiredApplicationResourceForPath(pathname: string): string | undefined {
  if (pathname === '/' || pathname === '') {
    return PATHNAME_TO_RESOURCE[''];
  }
  if (pathname.includes('/aiworkers/list/journeys')) {
    return PATHNAME_TO_RESOURCE['aiworkers'];
  }
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? '';
  return PATHNAME_TO_RESOURCE[firstSegment];
}

const Layout = ({ children }: Props) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { isDark } = useTheme();
  const logoSrc = isDark ? '/Logo-dark.svg' : '/Logo.svg';
  const { logout, loginWithRedirect } = useAuth0();
  const dispatch = useAppDispatch();
  const pathname = useLocation().pathname;
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const authToken = useSelector((state: RootState) => state.auth.token);
  const decodedAuthToken = useMemo(() => (authToken ? decodeJWT(authToken) : null), [authToken]);
  const idpTenantId = String(decodedAuthToken?.tenant_id ?? decodedAuthToken?.['https://circuitry.ai/tenant_id'] ?? '');
  const idpCustomer = String(decodedAuthToken?.org_name ?? '');
  const width = useScreenWidth();
  const isMobile = width < 640;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => isMobile || JSON.parse(localStorage.getItem("sidenavCollapsed") ?? "true"));
  const [collapsedDueToSettings, setCollapsedDueToSettings] = useState<boolean>(false);
  const [blockedResource, setBlockedResource] = useState<string | null>(null);
  const { refetch: refetchUserResources } = useGetFgaUserResourcesQuery(userId || '', {
    skip: !userId,
  });

  const handleToggleSidenav = () => {
    if (sidenavRef.current) {
      sidenavRef.current?.toggle();
    }
  };

  const sidebarItems = SidebarItems(isCollapsed, handleToggleSidenav);
  const pathnameRef = useRef(pathname);
  const sidenavRef = useRef<{ toggle: () => void } | null>(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const currentLocation = pathnameRef.current.split('/')[1] ?? 'dashboard';
  const isChatView = pathname.includes('/advisors/') && search.includes('tab=converse');

  // Collapse sidebar when entering Settings; expand when leaving if we had collapsed it for Settings
  useEffect(() => {
    if (pathname.startsWith('/settings')) {
      if (!isCollapsed) {
        setCollapsedDueToSettings(true);
        setIsCollapsed(true);
      }
    } else {
      if (collapsedDueToSettings) {
        setIsCollapsed(false);
        setCollapsedDueToSettings(false);
      }
    }
  }, [pathname]);

  // Check access on every pathname change (navigation)
  useEffect(() => {
    pathnameRef.current = pathname;
    setBlockedResource(null);

    if (!userId) {
      return;
    }

    const resourceName = getRequiredApplicationResourceForPath(pathname);
    if (!resourceName) {
      return;
    }

    let cancelled = false;

    const checkAccess = async () => {
      const result = await refetchUserResources();
      if (cancelled) return;

      const resourcesData = result.data?.data?.resources;

      if (!resourcesData?.application_resources) {
        return;
      }

      const resources = resourcesData.application_resources;
      const matchedResource = resources.find(
        (resource: any) => {
          const idMatch = resource.id?.toLowerCase() === resourceName.toLowerCase();
          const nameMatch = resource.name?.toLowerCase() === resourceName.toLowerCase();
          const relationMatch = resource.relation === 'view';
          return (idMatch || nameMatch) && relationMatch;
        }
      );

      if (!matchedResource) {
        setBlockedResource(resourceName);
      }
    };

    void checkAccess();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, userId]);

  useEffect(() => {
    document.title = customTitles[currentLocation] || 'Circuitry AI';
  }, [currentLocation]);

  const onSideNavClick = (e: PressEvent) => {
    // Use 'as HTMLElement' to access dataset (fix TS error)
    const clickedKey = (e.target as HTMLElement).dataset.key ?? 'dashboard';
    document.title = customTitles[clickedKey] || 'Circuitry AI';

    if (clickedKey === "help") {
      let helpUrl = "https://customer.circuitry.ai/knowledge-base";
      const currentPath = pathnameRef.current;
      if (currentPath.includes("/createadvisor")) {
        helpUrl += "/getting-started-with-product-Advisor";
      } else if (currentPath.includes("/advisors")) {
        helpUrl += "/viewing-and-editing-advisors";
      } else if (currentPath.includes("/conversations")) {
        helpUrl += "/viewing-conversation-history";
      } else if (currentPath.includes("/knowledge")) {
        helpUrl += "/using-the-knowledge-base";
      } else if (currentPath.includes("/settings")) {
        helpUrl += "/settings";
      } else {
        helpUrl += "/dashboard-view";
      }
      window.open(helpUrl, "_blank");
      return;
    }

    if (clickedKey === 'toggle') {
      handleToggleSidenav();
      return;
    }

    // Access check happens in useEffect on pathname change
    // Navigation will trigger pathname change which will check access
    if (isOpen) {
      onOpenChange();  // This closes the modal
    }
  };
  if (pathname.includes('/file/')) {
    return <div>{children}</div>;
  }

  const handleCollapse = (isCollapsed: boolean) => {
    setIsCollapsed(isCollapsed);
    (!isMobile) && localStorage.setItem("sidenavCollapsed", JSON.stringify(isCollapsed));
  };

  const handleOrganisationSwitch = (name: string) => {
    dispatch(clearTenantDetailsResponse());
    sessionStorage.removeItem('advisor_id');  // Remove advisor_id from session storage
    loginWithRedirect({
      authorizationParams: {
        organization: name
      }
    });
  };

  const userLogout = () => {
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  const sidenavProps = {
    isOpen,
    onOpenChange,
    onClick: onSideNavClick,
    items: sidebarItems,
    onCollapseChange: handleCollapse,
    switchOrganisation: handleOrganisationSwitch,
    doLogout: userLogout,
    isCompact: isCollapsed,
  };

  const mainContent =
    blockedResource !== null ? (
      <AccessBlocked resourceName={blockedResource ?? undefined} />
    ) : (
      children
    );

  return (
    <div className="flex flex-col h-screen ">

      <div className="flex fixed left-0 h-full w-full z-10">
        <div
          className='bg-content1 border-r border-default-200 flex flex-col justify-between h-full dark:bg-[#010409] dark:border-[#30363d]'
        >
          {!isCollapsed && <div className='hidden sm:block'>
            <div className={`w-full flex justify-between gap-3 px-2 py-3 items-center ${!isCollapsed && 'border-b'}`}>
              <CtyButton className='flex gap-4 items-center h-[40px]' variant='light' disableRipple={true} size="sm" onPress={() => navigate('/')}>
                <Image src={logoSrc} alt="Logo" width={150} />
              </CtyButton>
              <CtyButton variant="light" size="sm" isIconOnly radius="full" disableRipple={true} onPress={handleToggleSidenav}>
                <Icon className="text-black dark:text-[#f5f5f5]" icon="hugeicons:layout-right" width="1.5em" height="1.5em" />
              </CtyButton>
            </div>
          </div>}
          <div className='flex-1 overflow-y-auto'>
            <Sidenav ref={sidenavRef} {...sidenavProps} />
          </div>
        </div>
        <div className={`flex flex-col h-full w-[100%]`}>
          <CtyHeader
            logo={logoSrc}
            onOpen={onOpen}
            isCollapsed={isCollapsed}
          />
          {!isChatView ? (
            <div className='flex-1 flex flex-col max-w-[99.5%] overflow-hidden'>
              <div className="flex-grow overflow-hidden">{mainContent}</div>
              <footer className="p-2 text-center text-xs text-default-500 border dark:text-[#8a8a8a] dark:border-[#30363d] dark:bg-[#010409]">
                Copyright Circuitry.ai {new Date().getFullYear()} / <a href="/privacy-policy" className="underline">Privacy Policy</a> / <ReleaseNotesFooterLink
                  tenantId={idpTenantId}
                  customer={idpCustomer}
                  token={authToken}
                  fallback={<>V {BUILD_NUMBER}</>}
                />
              </footer>
            </div>
          ) : (
            <div className='flex-1 overflow-hidden layout-content'>{mainContent}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Layout;




